/**
 * Static BY_DIFFICULTIES catalog structure (slots, limits, order, titles).
 *
 * `TEST_GROUP_AND_TEST_CONFIG_V1`: bands, testOrder, per-test metadata. Images on rows here are
 * used only when regenerating `catalogImageConfigByDifficulties.v1.ts` — runtime image lookup is
 * `catalogImageLookup.v1.ts` (two separate configs: BY_DIFFICULTIES + BY_TOPICS).
 *
 * Docs: docs/docsForUI/CATALOG_STATIC_IMAGES_V1.md
 */
import { DIFFICULTIES, type Difficulty, type Topic } from '../Pages/DataBaseV3/Questions/QuestionsConstant';
export { catalogImageForTemplateKey, type CatalogImageRef } from './catalogImageLookup.v1';

export const TEST_GROUP_AND_TEST_CONFIG_VERSION = 'test-group-and-test.v2';

export type CatalogTestConfigEntry = {
  key: string;
  topic: Topic;
  slotIndex: number;
  name: string;
  templateKey: string;
  orderIndex: number;
  limit: number;
  priority: Difficulty;
  imageStoragePath: string;
  imageUrl: string;
};

export type CatalogTestGroupConfig = {
  key: string;
  name: string;
  templateKey: string;
  priority: Difficulty;
  imageUrl: string;
  imageStoragePath: string;
};

export type CatalogBandConfig = {
  testGroup: CatalogTestGroupConfig;
  testOrder: readonly string[];
  tests: Record<string, CatalogTestConfigEntry>;
};

export const TEST_GROUP_AND_TEST_CONFIG_V1: Record<'soft' | 'medium' | 'hard', CatalogBandConfig> = {
  soft: {
    testGroup: {
      key: 'soft',
      name: 'Soft',
      templateKey: 'difficulty.soft',
      priority: 'soft',
      imageStoragePath: 'images/catalog/difficulty.soft.jpg',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fdifficulty.soft.jpg?alt=media&token=348d2f79-cd10-4012-a8d0-245754933f6d',
    },
    testOrder: [
      'priority-on-road-1',
      'sharing-pedestrians-1',
      'maneuvers-signaling-1',
      'passing-1',
      'parking-regulations-1',
      'road-signs-1',
      'road-markings-1',
      'braking-1',
      'traffic-regulations-1',
      'driving-1',
      'road-signs-2',
      'traffic-regulations-2',
    ],
    tests: {
      'priority-on-road-1': {
        key: 'priority-on-road-1',
        topic: 'priority-on-road',
        slotIndex: 1,
        name: "Priority on the road",
        templateKey: 'difficulty.soft.topic.priority-on-road.slot.1',
        orderIndex: 1,
        limit: 45,
        priority: 'soft',
        imageStoragePath: 'images/catalog/priority-on-road-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fpriority-on-road-1.jpg?alt=media&token=d42d945c-02ff-4c7a-b526-1501707dd401',
      },
      'sharing-pedestrians-1': {
        key: 'sharing-pedestrians-1',
        topic: 'sharing-pedestrians',
        slotIndex: 1,
        name: 'Sharing with pedestrians',
        templateKey: 'difficulty.soft.topic.sharing-pedestrians.slot.1',
        orderIndex: 2,
        limit: 40,
        priority: 'soft',
        imageStoragePath: 'images/catalog/sharing-pedestrians-1.jpg',
        imageUrl:
          'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fsharing-pedestrians-1.jpg?alt=media&token=edd39ad7-5147-42e1-b2dd-1007a9c1e424',
      },
      'maneuvers-signaling-1': {
        key: 'maneuvers-signaling-1',
        topic: 'maneuvers-signaling',
        slotIndex: 1,
        name: "Maneuvers and signaling",
        templateKey: 'difficulty.soft.topic.maneuvers-signaling.slot.1',
        orderIndex: 3,
        limit: 45,
        priority: 'soft',
        imageStoragePath: 'images/catalog/maneuvers-signaling-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fmaneuvers-signaling-1.jpg?alt=media&token=14ffeb48-8762-4f24-8b51-bd67756c33ad',
      },
      'passing-1': {
        key: 'passing-1',
        topic: 'passing',
        slotIndex: 1,
        name: "Passing",
        templateKey: 'difficulty.soft.topic.passing.slot.1',
        orderIndex: 4,
        limit: 45,
        priority: 'soft',
        imageStoragePath: 'images/catalog/passing-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fpassing-1.jpg?alt=media&token=d7c299dc-c8c9-456f-828a-cd4a6cb44125',
      },
      'parking-regulations-1': {
        key: 'parking-regulations-1',
        topic: 'parking-regulations',
        slotIndex: 1,
        name: "Parking regulations",
        templateKey: 'difficulty.soft.topic.parking-regulations.slot.1',
        orderIndex: 5,
        limit: 45,
        priority: 'soft',
        imageStoragePath: 'images/catalog/parking-regulations-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fparking-regulations-1.jpg?alt=media&token=9b18ddcb-377f-4d01-a591-f594f9f9e3ea',
      },
      'road-signs-1': {
        key: 'road-signs-1',
        topic: 'road-signs',
        slotIndex: 1,
        name: "Road signs",
        templateKey: 'difficulty.soft.topic.road-signs.slot.1',
        orderIndex: 6,
        limit: 80,
        priority: 'soft',
        imageStoragePath: 'images/catalog/road-signs-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Froad-signs-1.jpg?alt=media&token=3ca7b20b-e08f-4521-bfe0-1807eb84746c',
      },
      'road-markings-1': {
        key: 'road-markings-1',
        topic: 'road-markings',
        slotIndex: 1,
        name: "Road markings",
        templateKey: 'difficulty.soft.topic.road-markings.slot.1',
        orderIndex: 7,
        limit: 45,
        priority: 'soft',
        imageStoragePath: 'images/catalog/road-markings-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Froad-markings-1.jpg?alt=media&token=9f9a3a80-ceb7-4811-a6a4-ac5dd4fc9ece',
      },
      'braking-1': {
        key: 'braking-1',
        topic: 'braking',
        slotIndex: 1,
        name: "Braking",
        templateKey: 'difficulty.soft.topic.braking.slot.1',
        orderIndex: 8,
        limit: 45,
        priority: 'soft',
        imageStoragePath: 'images/catalog/braking-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fbraking-1.jpg?alt=media&token=a15173af-47dd-4ef3-a703-696ab809d0e2',
      },
      'traffic-regulations-1': {
        key: 'traffic-regulations-1',
        topic: 'traffic-regulations',
        slotIndex: 1,
        name: "Traffic regulations",
        templateKey: 'difficulty.soft.topic.traffic-regulations.slot.1',
        orderIndex: 9,
        limit: 45,
        priority: 'soft',
        imageStoragePath: 'images/catalog/traffic-regulations-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Ftraffic-regulations-1.jpg?alt=media&token=2deb0040-3685-4cbd-b388-c8185669bd85',
      },
      'driving-1': {
        key: 'driving-1',
        topic: 'driving',
        slotIndex: 1,
        name: "Driving",
        templateKey: 'difficulty.soft.topic.driving.slot.1',
        orderIndex: 10,
        limit: 90,
        priority: 'soft',
        imageStoragePath: 'images/catalog/driving-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fdriving-1.jpg?alt=media&token=301a0086-72d7-4e34-8411-1302a43c0f6e',
      },
      'road-signs-2': {
        key: 'road-signs-2',
        topic: 'road-signs',
        slotIndex: 2,
        name: "Road signs 2",
        templateKey: 'difficulty.soft.topic.road-signs.slot.2',
        orderIndex: 11,
        limit: 80,
        priority: 'soft',
        imageStoragePath: 'images/catalog/road-signs-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Froad-signs-2.jpg?alt=media&token=4613aae8-6d84-466d-af45-bd3884a16f2b',
      },
      'traffic-regulations-2': {
        key: 'traffic-regulations-2',
        topic: 'traffic-regulations',
        slotIndex: 2,
        name: "Traffic regulations 2",
        templateKey: 'difficulty.soft.topic.traffic-regulations.slot.2',
        orderIndex: 12,
        limit: 45,
        priority: 'soft',
        imageStoragePath: 'images/catalog/traffic-regulations-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Ftraffic-regulations-2.jpg?alt=media&token=b987c373-0569-43e3-b3f3-a6a66d9bf589',
      },
    },
  },
  medium: {
    testGroup: {
      key: 'medium',
      name: 'Medium',
      templateKey: 'difficulty.medium',
      priority: 'medium',
      imageStoragePath: 'images/catalog/difficulty.medium.jpg',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fdifficulty.medium.jpg?alt=media&token=083bb717-3ab5-42f5-8378-b3861f937bb6',
    },
    testOrder: [
      'priority-on-road-2',
      'sharing-school-bus-1',
      'maneuvers-signaling-2',
      'passing-2',
      'parking-regulations-2',
      'road-signs-3',
      'road-markings-2',
      'roundabouts-1',
      'use-of-lights-1',
      'braking-2',
      'railroad-crossings-1',
      'traffic-regulations-3',
      'freeways-1',
      'emergency-on-road-1',
      'road-work-1',
      'weather-conditions-1',
      'drinking-driving-1',
      'driving-2',
      'driver-license-1',
      'road-signs-4',
      'traffic-regulations-4',
      'driving-3',
    ],
    tests: {
      'priority-on-road-2': {
        key: 'priority-on-road-2',
        topic: 'priority-on-road',
        slotIndex: 1,
        name: "Priority on the road 2",
        templateKey: 'difficulty.medium.topic.priority-on-road.slot.1',
        orderIndex: 1,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/priority-on-road-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fpriority-on-road-2.jpg?alt=media&token=c5e14479-e5cb-4d6a-84ee-974dea3ca8d7',
      },
      'sharing-school-bus-1': {
        key: 'sharing-school-bus-1',
        topic: 'sharing-school-bus',
        slotIndex: 1,
        name: 'Sharing with school bus',
        templateKey: 'difficulty.medium.topic.sharing-school-bus.slot.1',
        orderIndex: 2,
        limit: 40,
        priority: 'medium',
        imageStoragePath: 'images/catalog/sharing-school-bus-1.jpg',
        imageUrl:
          'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fsharing-school-bus-1.jpg?alt=media&token=6b3962b0-6d35-461b-938f-cbd64bd3cdf7',
      },
      'maneuvers-signaling-2': {
        key: 'maneuvers-signaling-2',
        topic: 'maneuvers-signaling',
        slotIndex: 1,
        name: "Maneuvers and signaling 2",
        templateKey: 'difficulty.medium.topic.maneuvers-signaling.slot.1',
        orderIndex: 3,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/maneuvers-signaling-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fmaneuvers-signaling-2.jpg?alt=media&token=1bbb4e9d-77a9-4db0-b45c-95319352bffb',
      },
      'passing-2': {
        key: 'passing-2',
        topic: 'passing',
        slotIndex: 1,
        name: "Passing 2",
        templateKey: 'difficulty.medium.topic.passing.slot.1',
        orderIndex: 4,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/passing-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fpassing-2.jpg?alt=media&token=fe83ed69-daa6-416c-8336-e8ad820ed267',
      },
      'parking-regulations-2': {
        key: 'parking-regulations-2',
        topic: 'parking-regulations',
        slotIndex: 1,
        name: "Parking regulations 2",
        templateKey: 'difficulty.medium.topic.parking-regulations.slot.1',
        orderIndex: 5,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/parking-regulations-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fparking-regulations-2.jpg?alt=media&token=38f36b2c-798d-4b39-a8da-81bb05312eb3',
      },
      'road-signs-3': {
        key: 'road-signs-3',
        topic: 'road-signs',
        slotIndex: 1,
        name: "Road signs 3",
        templateKey: 'difficulty.medium.topic.road-signs.slot.1',
        orderIndex: 6,
        limit: 80,
        priority: 'medium',
        imageStoragePath: 'images/catalog/road-signs-3.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Froad-signs-3.jpg?alt=media&token=92c19d0a-cd16-4f88-8d48-d05e58473e1b',
      },
      'road-markings-2': {
        key: 'road-markings-2',
        topic: 'road-markings',
        slotIndex: 1,
        name: "Road markings 2",
        templateKey: 'difficulty.medium.topic.road-markings.slot.1',
        orderIndex: 7,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/road-markings-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Froad-markings-1.jpg?alt=media&token=9f9a3a80-ceb7-4811-a6a4-ac5dd4fc9ece',
      },
      'roundabouts-1': {
        key: 'roundabouts-1',
        topic: 'roundabouts',
        slotIndex: 1,
        name: "Roundabouts",
        templateKey: 'difficulty.medium.topic.roundabouts.slot.1',
        orderIndex: 8,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/roundabouts-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Froundabouts-2.jpg?alt=media&token=70f11ff6-739d-418a-8ace-0635b941d3cd',
      },
      'use-of-lights-1': {
        key: 'use-of-lights-1',
        topic: 'use-of-lights',
        slotIndex: 1,
        name: "Use of lights",
        templateKey: 'difficulty.medium.topic.use-of-lights.slot.1',
        orderIndex: 9,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/use-of-lights-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fuse-of-lights-2.jpg?alt=media&token=5e6c8c6c-9225-47f0-a4fe-04cc017b92ec',
      },
      'braking-2': {
        key: 'braking-2',
        topic: 'braking',
        slotIndex: 1,
        name: "Braking 2",
        templateKey: 'difficulty.medium.topic.braking.slot.1',
        orderIndex: 10,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/braking-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fbraking-2.jpg?alt=media&token=16da5f6c-873d-43d5-93ab-59463328966a',
      },
      'railroad-crossings-1': {
        key: 'railroad-crossings-1',
        topic: 'railroad-crossings',
        slotIndex: 1,
        name: "Railroad crossings",
        templateKey: 'difficulty.medium.topic.railroad-crossings.slot.1',
        orderIndex: 11,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/railroad-crossings-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Frailroad-crossings-2.jpg?alt=media&token=bf140e94-fe38-447d-bd3b-ae7c11433f4b',
      },
      'traffic-regulations-3': {
        key: 'traffic-regulations-3',
        topic: 'traffic-regulations',
        slotIndex: 1,
        name: "Traffic regulations 3",
        templateKey: 'difficulty.medium.topic.traffic-regulations.slot.1',
        orderIndex: 12,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/traffic-regulations-3.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Ftraffic-regulations-3.jpg?alt=media&token=36e5ee2c-c183-4082-ade3-e47dfa74e8ca',
      },
      'freeways-1': {
        key: 'freeways-1',
        topic: 'freeways',
        slotIndex: 1,
        name: "Freeways",
        templateKey: 'difficulty.medium.topic.freeways.slot.1',
        orderIndex: 13,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/freeways-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Ffreeways-1.jpg?alt=media&token=acf99eaa-b711-4254-8ec2-a7a719970601',
      },
      'emergency-on-road-1': {
        key: 'emergency-on-road-1',
        topic: 'emergency-on-road',
        slotIndex: 1,
        name: "Emergency on road",
        templateKey: 'difficulty.medium.topic.emergency-on-road.slot.1',
        orderIndex: 14,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/emergency-on-road-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Femergency-on-road-1.jpg?alt=media&token=b00f953d-730a-4268-b491-98eaf1feb747',
      },
      'road-work-1': {
        key: 'road-work-1',
        topic: 'road-work',
        slotIndex: 1,
        name: "Road work",
        templateKey: 'difficulty.medium.topic.road-work.slot.1',
        orderIndex: 15,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/road-work-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Froad-work-1.jpg?alt=media&token=12dbdfe9-3c88-4310-9224-a728a822542e',
      },
      'weather-conditions-1': {
        key: 'weather-conditions-1',
        topic: 'weather-conditions',
        slotIndex: 1,
        name: "Weather conditions",
        templateKey: 'difficulty.medium.topic.weather-conditions.slot.1',
        orderIndex: 16,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/weather-conditions-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fweather-conditions-1.jpg?alt=media&token=6e2ef2ad-1292-47db-8637-dce7631b75e7',
      },
      'drinking-driving-1': {
        key: 'drinking-driving-1',
        topic: 'drinking-driving',
        slotIndex: 1,
        name: "Drinking and driving",
        templateKey: 'difficulty.medium.topic.drinking-driving.slot.1',
        orderIndex: 17,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/drinking-driving-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fdrinking-driving-1.jpg?alt=media&token=3a1bb696-fdb6-4e08-adf4-5e43de3dbb19',
      },
      'driving-2': {
        key: 'driving-2',
        topic: 'driving',
        slotIndex: 1,
        name: "Driving 2",
        templateKey: 'difficulty.medium.topic.driving.slot.1',
        orderIndex: 18,
        limit: 90,
        priority: 'medium',
        imageStoragePath: 'images/catalog/driving-3.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fdriving-3.jpg?alt=media&token=b3fd5999-18c5-4a71-9676-1e83f98be95a',
      },
      'driver-license-1': {
        key: 'driver-license-1',
        topic: 'driver-license',
        slotIndex: 1,
        name: "Driver license",
        templateKey: 'difficulty.medium.topic.driver-license.slot.1',
        orderIndex: 19,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/driver-license-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fdriver-license-2.jpg?alt=media&token=d58efbdf-99b0-44c8-a513-89e6d3ed82d1',
      },
      'road-signs-4': {
        key: 'road-signs-4',
        topic: 'road-signs',
        slotIndex: 2,
        name: "Road signs 4",
        templateKey: 'difficulty.medium.topic.road-signs.slot.2',
        orderIndex: 20,
        limit: 80,
        priority: 'medium',
        imageStoragePath: 'images/catalog/road-signs-4.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Froad-signs-4.jpg?alt=media&token=1c451abc-bbf7-4589-8cb7-b3400e68557d',
      },
      'traffic-regulations-4': {
        key: 'traffic-regulations-4',
        topic: 'traffic-regulations',
        slotIndex: 2,
        name: "Traffic regulations 4",
        templateKey: 'difficulty.medium.topic.traffic-regulations.slot.2',
        orderIndex: 21,
        limit: 45,
        priority: 'medium',
        imageStoragePath: 'images/catalog/traffic-regulations-4.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Ftraffic-regulations-4.jpg?alt=media&token=4fa147ca-d957-44c4-9485-42c1a6464396',
      },
      'driving-3': {
        key: 'driving-3',
        topic: 'driving',
        slotIndex: 2,
        name: "Driving 3",
        templateKey: 'difficulty.medium.topic.driving.slot.2',
        orderIndex: 22,
        limit: 90,
        priority: 'medium',
        imageStoragePath: 'images/catalog/driving-4.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fdriving-4.jpg?alt=media&token=ac313495-68e6-435c-a412-9b99b9c0dc72',
      },
    },
  },
  hard: {
    testGroup: {
      key: 'hard',
      name: 'Hard',
      templateKey: 'difficulty.hard',
      priority: 'hard',
      imageStoragePath: 'images/catalog/difficulty.hard.jpg',
      imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fdifficulty.hard.jpg?alt=media&token=5c52f886-54a8-4b51-9b29-89b48c40cb8e',
    },
    testOrder: [
      'sharing-road-1',
      'road-signs-5',
      'roundabouts-2',
      'use-of-lights-2',
      'railroad-crossings-2',
      'traffic-regulations-5',
      'fines-and-limits-1',
      'freeways-2',
      'emergency-on-road-2',
      'road-work-2',
      'weather-conditions-2',
      'drinking-driving-2',
      'driving-4',
      'driver-license-2',
      'sharing-road-2',
      'road-signs-6',
    ],
    tests: {
      'sharing-road-1': {
        key: 'sharing-road-1',
        topic: 'sharing-road',
        slotIndex: 1,
        name: "Sharing the road",
        templateKey: 'difficulty.hard.topic.sharing-road.slot.1',
        orderIndex: 1,
        limit: 40,
        priority: 'hard',
        imageStoragePath: 'images/catalog/sharing-road-3.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fsharing-road-3.jpg?alt=media&token=3d0fef27-b4bc-4125-ab75-05fafadd1414',
      },
      'sharing-road-2': {
        key: 'sharing-road-2',
        topic: 'sharing-road',
        slotIndex: 2,
        name: "Sharing the road 2",
        templateKey: 'difficulty.hard.topic.sharing-road.slot.2',
        orderIndex: 15,
        limit: 40,
        priority: 'hard',
        imageStoragePath: 'images/catalog/sharing-road-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fsharing-road-2.jpg?alt=media&token=0c7ee03f-0b1a-4a66-b9ff-fd778079d439',
      },
      'road-signs-5': {
        key: 'road-signs-5',
        topic: 'road-signs',
        slotIndex: 1,
        name: "Road signs 5",
        templateKey: 'difficulty.hard.topic.road-signs.slot.1',
        orderIndex: 2,
        limit: 80,
        priority: 'hard',
        imageStoragePath: 'images/catalog/road-signs-5.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Froad-signs-5.jpg?alt=media&token=784fadc0-50c7-4f76-b008-06bbda604d28',
      },
      'roundabouts-2': {
        key: 'roundabouts-2',
        topic: 'roundabouts',
        slotIndex: 1,
        name: "Roundabouts 2",
        templateKey: 'difficulty.hard.topic.roundabouts.slot.1',
        orderIndex: 3,
        limit: 45,
        priority: 'hard',
        imageStoragePath: 'images/catalog/roundabouts-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Froundabouts-2.jpg?alt=media&token=70f11ff6-739d-418a-8ace-0635b941d3cd',
      },
      'use-of-lights-2': {
        key: 'use-of-lights-2',
        topic: 'use-of-lights',
        slotIndex: 1,
        name: "Use of lights 2",
        templateKey: 'difficulty.hard.topic.use-of-lights.slot.1',
        orderIndex: 4,
        limit: 45,
        priority: 'hard',
        imageStoragePath: 'images/catalog/use-of-lights-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fuse-of-lights-2.jpg?alt=media&token=5e6c8c6c-9225-47f0-a4fe-04cc017b92ec',
      },
      'railroad-crossings-2': {
        key: 'railroad-crossings-2',
        topic: 'railroad-crossings',
        slotIndex: 1,
        name: "Railroad crossings 2",
        templateKey: 'difficulty.hard.topic.railroad-crossings.slot.1',
        orderIndex: 5,
        limit: 45,
        priority: 'hard',
        imageStoragePath: 'images/catalog/railroad-crossings-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Frailroad-crossings-2.jpg?alt=media&token=bf140e94-fe38-447d-bd3b-ae7c11433f4b',
      },
      'traffic-regulations-5': {
        key: 'traffic-regulations-5',
        topic: 'traffic-regulations',
        slotIndex: 1,
        name: "Traffic regulations 5",
        templateKey: 'difficulty.hard.topic.traffic-regulations.slot.1',
        orderIndex: 6,
        limit: 45,
        priority: 'hard',
        imageStoragePath: 'images/catalog/traffic-regulations-5.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Ftraffic-regulations-5.jpg?alt=media&token=53f9bd8b-77ca-4905-be5e-ec8db5da3691',
      },
      'fines-and-limits-1': {
        key: 'fines-and-limits-1',
        topic: 'fines-and-limits',
        slotIndex: 1,
        name: "Fines and limits",
        templateKey: 'difficulty.hard.topic.fines-and-limits.slot.1',
        orderIndex: 7,
        limit: 40,
        priority: 'hard',
        imageStoragePath: 'images/catalog/fines-and-limits-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Ffines-and-limits-1.jpg?alt=media&token=ba3c0eec-4c2a-4cf1-9d15-4b4c5d253b5d',
      },
      'freeways-2': {
        key: 'freeways-2',
        topic: 'freeways',
        slotIndex: 1,
        name: "Freeways 2",
        templateKey: 'difficulty.hard.topic.freeways.slot.1',
        orderIndex: 8,
        limit: 45,
        priority: 'hard',
        imageStoragePath: 'images/catalog/freeways-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Ffreeways-1.jpg?alt=media&token=acf99eaa-b711-4254-8ec2-a7a719970601',
      },
      'emergency-on-road-2': {
        key: 'emergency-on-road-2',
        topic: 'emergency-on-road',
        slotIndex: 1,
        name: "Emergency on road 2",
        templateKey: 'difficulty.hard.topic.emergency-on-road.slot.1',
        orderIndex: 9,
        limit: 45,
        priority: 'hard',
        imageStoragePath: 'images/catalog/emergency-on-road-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Femergency-on-road-2.jpg?alt=media&token=3e6b3fa5-8975-406b-8de1-2231929f49f1',
      },
      'road-work-2': {
        key: 'road-work-2',
        topic: 'road-work',
        slotIndex: 1,
        name: "Road work 2",
        templateKey: 'difficulty.hard.topic.road-work.slot.1',
        orderIndex: 10,
        limit: 45,
        priority: 'hard',
        imageStoragePath: 'images/catalog/road-work-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Froad-work-1.jpg?alt=media&token=12dbdfe9-3c88-4310-9224-a728a822542e',
      },
      'weather-conditions-2': {
        key: 'weather-conditions-2',
        topic: 'weather-conditions',
        slotIndex: 1,
        name: "Weather conditions 2",
        templateKey: 'difficulty.hard.topic.weather-conditions.slot.1',
        orderIndex: 11,
        limit: 45,
        priority: 'hard',
        imageStoragePath: 'images/catalog/weather-conditions-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fweather-conditions-1.jpg?alt=media&token=6e2ef2ad-1292-47db-8637-dce7631b75e7',
      },
      'drinking-driving-2': {
        key: 'drinking-driving-2',
        topic: 'drinking-driving',
        slotIndex: 1,
        name: "Drinking and driving 2",
        templateKey: 'difficulty.hard.topic.drinking-driving.slot.1',
        orderIndex: 12,
        limit: 45,
        priority: 'hard',
        imageStoragePath: 'images/catalog/drinking-driving-1.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fdrinking-driving-1.jpg?alt=media&token=3a1bb696-fdb6-4e08-adf4-5e43de3dbb19',
      },
      'driving-4': {
        key: 'driving-4',
        topic: 'driving',
        slotIndex: 1,
        name: "Driving 4",
        templateKey: 'difficulty.hard.topic.driving.slot.1',
        orderIndex: 13,
        limit: 90,
        priority: 'hard',
        imageStoragePath: 'images/catalog/driving-5.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fdriving-5.jpg?alt=media&token=35d681cc-d1a0-4cdd-875a-c0264f9b6fb1',
      },
      'driver-license-2': {
        key: 'driver-license-2',
        topic: 'driver-license',
        slotIndex: 1,
        name: "Driver license 2",
        templateKey: 'difficulty.hard.topic.driver-license.slot.1',
        orderIndex: 14,
        limit: 45,
        priority: 'hard',
        imageStoragePath: 'images/catalog/driver-license-2.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Fdriver-license-2.jpg?alt=media&token=d58efbdf-99b0-44c8-a513-89e6d3ed82d1',
      },
      'road-signs-6': {
        key: 'road-signs-6',
        topic: 'road-signs',
        slotIndex: 2,
        name: "Road signs 6",
        templateKey: 'difficulty.hard.topic.road-signs.slot.2',
        orderIndex: 16,
        limit: 80,
        priority: 'hard',
        imageStoragePath: 'images/catalog/road-signs-5.jpg',
        imageUrl: 'https://firebasestorage.googleapis.com/v0/b/vehiwheelprod/o/images%2Fcatalog%2Froad-signs-5.jpg?alt=media&token=784fadc0-50c7-4f76-b008-06bbda604d28',
      },
    },
  },
};

export type TargetManifestSlot = {
  orderIndex: number;
  difficulty: Difficulty;
  topic: Topic;
  slotIndex: number;
  testTitle: string;
  templateKey: string;
};

export const TARGET_BY_DIFFICULTIES_V1: TargetManifestSlot[] = DIFFICULTIES.flatMap((difficulty) => {
  const band = TEST_GROUP_AND_TEST_CONFIG_V1[difficulty];
  return band.testOrder.map((key) => {
    const t = band.tests[key]!;
    return {
      orderIndex: t.orderIndex,
      difficulty,
      topic: t.topic,
      slotIndex: t.slotIndex,
      testTitle: t.name,
      templateKey: t.templateKey,
    };
  });
});

const manifestOrderMap = new Map<string, number>();
for (const slot of TARGET_BY_DIFFICULTIES_V1) {
  manifestOrderMap.set(
    `${slot.difficulty}\u0001${slot.topic}\u0001${slot.slotIndex}`,
    slot.orderIndex
  );
}

export function getManifestLearnerOrderIndex(
  difficulty: Difficulty,
  topic: Topic,
  slotIndex: number
): number {
  return manifestOrderMap.get(`${difficulty}\u0001${topic}\u0001${slotIndex}`) ?? 9999;
}

export function getCatalogTestConfig(
  difficulty: Difficulty,
  topic: Topic,
  slotIndex: number
): CatalogTestConfigEntry | undefined {
  const band = TEST_GROUP_AND_TEST_CONFIG_V1[difficulty];
  for (const key of band.testOrder) {
    const entry = band.tests[key]!;
    if (entry.topic === topic && entry.slotIndex === slotIndex) {
      return entry;
    }
  }
  return undefined;
}

/** First manifest slot for a topic (Soft → Medium → Hard) — titles / ordering only (not images). */
export function getCatalogFirstSlotConfigForTopic(topic: Topic): CatalogTestConfigEntry | undefined {
  for (const difficulty of DIFFICULTIES) {
    const band = TEST_GROUP_AND_TEST_CONFIG_V1[difficulty];
    for (const mapKey of band.testOrder) {
      const entry = band.tests[mapKey]!;
      if (entry.topic === topic) return entry;
    }
  }
  return undefined;
}

export function getCatalogDifficultyBandGroup(difficulty: Difficulty): CatalogTestGroupConfig {
  return TEST_GROUP_AND_TEST_CONFIG_V1[difficulty].testGroup;
}

export function staticManifestTestsForDifficulty(difficulty: Difficulty) {
  const band = TEST_GROUP_AND_TEST_CONFIG_V1[difficulty];
  return band.testOrder.map((key) => {
    const t = band.tests[key]!;
    return {
      orderIndex: t.orderIndex,
      difficulty,
      topic: t.topic,
      slotIndex: t.slotIndex,
      testTitle: t.name,
      templateKey: t.templateKey,
      imageFileName: t.imageStoragePath.replace(/^images\/catalog\//, ''),
      image: { imageUrl: t.imageUrl, imageStoragePath: t.imageStoragePath },
    };
  });
}

export const BY_TOPICS_GROUP_ORDER_V1: Topic[] = [
  'priority-on-road',
  'sharing-pedestrians',
  'sharing-school-bus',
  'sharing-road',
  'maneuvers-signaling',
  'passing',
  'parking-regulations',
  'road-signs',
  'road-markings',
  'roundabouts',
  'use-of-lights',
  'braking',
  'railroad-crossings',
  'traffic-regulations',
  'fines-and-limits',
  'freeways',
  'emergency-on-road',
  'road-work',
  'weather-conditions',
  'drinking-driving',
  'driving',
  'driver-license',
];

export const TOPIC_BAND_RULES_V1: Partial<Record<Topic, Difficulty[]>> = {
  'emergency-on-road': ['medium', 'hard'],
  'maneuvers-signaling': ['soft', 'medium'],
  'priority-on-road': ['soft', 'medium'],
  'weather-conditions': ['medium', 'hard'],
  'freeways': ['medium', 'hard'],
  'drinking-driving': ['medium', 'hard'],
  'parking-regulations': ['soft', 'medium'],
  'passing': ['soft', 'medium'],
  'braking': ['soft', 'medium'],
  'road-markings': ['soft', 'medium'],
  'railroad-crossings': ['medium', 'hard'],
  'road-work': ['medium', 'hard'],
  'use-of-lights': ['medium', 'hard'],
  'roundabouts': ['medium', 'hard'],
  'driver-license': ['medium', 'hard'],
  'fines-and-limits': ['hard'],
  'sharing-pedestrians': ['soft'],
  'sharing-school-bus': ['medium'],
  'sharing-road': ['hard'],
};

export function allowedBandsForTopic(topic: Topic): Difficulty[] {
  return TOPIC_BAND_RULES_V1[topic] ?? [...DIFFICULTIES];
}

export function slotsForTopicInBand(topic: Topic, difficulty: Difficulty): TargetManifestSlot[] {
  return TARGET_BY_DIFFICULTIES_V1.filter((s) => s.topic === topic && s.difficulty === difficulty);
}

export function manifestSlotsForTopic(topic: Topic): TargetManifestSlot[] {
  return TARGET_BY_DIFFICULTIES_V1.filter((s) => s.topic === topic);
}

export function manifestSlotCountForTopic(topic: Topic): number {
  return manifestSlotsForTopic(topic).length;
}

/** Find manifest row by config map key (e.g. `road-work-1`). */
export function getCatalogTestConfigByKey(configKey: string): CatalogTestConfigEntry | undefined {
  for (const band of DIFFICULTIES) {
    const entry = TEST_GROUP_AND_TEST_CONFIG_V1[band].tests[configKey];
    if (entry) return entry;
  }
  return undefined;
}

export function formatTargetCatalogTreeText(): string {
  const lines: string[] = [
    '=== TEST_GROUP_AND_TEST_CONFIG v1 ===',
    `BY_DIFFICULTIES: ${TARGET_BY_DIFFICULTIES_V1.length} tests (soft ${TEST_GROUP_AND_TEST_CONFIG_V1.soft.testOrder.length}, medium ${TEST_GROUP_AND_TEST_CONFIG_V1.medium.testOrder.length}, hard ${TEST_GROUP_AND_TEST_CONFIG_V1.hard.testOrder.length})`,
    '',
    '--- BY_DIFFICULTIES ---',
  ];
  for (const d of DIFFICULTIES) {
    const band = TEST_GROUP_AND_TEST_CONFIG_V1[d];
    lines.push(`\n## ${d}`);
    for (const key of band.testOrder) {
      const t = band.tests[key]!;
      lines.push(`  ${t.orderIndex}. ${t.name} limit=${t.limit}`);
    }
  }
  return lines.join('\n');
}
